import {
  PITCH_DIMENSIONS,
  ScreenCoordinate,
  VIEWPORT_DIMENSIONS,
  gameToScreenCoords,
} from "@Components/game/session-results/feedback/visualization/constants";
import {
  ReplayPassType,
  buildReplayPreviewPayload,
  getAlternativeDecisions,
  type ReplayDecision,
} from "@hooks/common/session/replay-decision-utils";
import { Graphics as PixiGraphics } from "@pixi/graphics";
import { Container, Graphics } from "@pixi/react";
import type { ReplayData } from "@Shared/types/ReplayData";

interface PassVisualizationProps {
  currentDecision: ReplayDecision | null;
  currentTime: number;
  selectedCard: number;
  replayData: ReplayData | null;
}

const PassVisualization = ({ currentDecision, currentTime, selectedCard, replayData }: PassVisualizationProps) => {
  if (!currentDecision || !replayData) return null;

  const { startTime, endTime } = currentDecision;
  const options = [currentDecision.decisions.user ?? null, ...getAlternativeDecisions(currentDecision)];

  const shouldShow = currentTime >= startTime && currentTime <= endTime;

  if (!shouldShow) return null;

  const applyLineStyle = (g: PixiGraphics, width: number, isSelected: boolean) => {
    const lineColor = isSelected ? 0xffffff : 0x888888;
    const lineAlpha = isSelected ? 1 : 0.6;
    g.lineStyle(width * VIEWPORT_DIMENSIONS.SCALE, lineColor, lineAlpha);
  };

  const isRenderableDistance = (distance: number) => Number.isFinite(distance) && distance > 0.001;

  const drawArrowhead = (g: PixiGraphics, end: ScreenCoordinate, angle: number, padding: number) => {
    const arrowLength = 12 * VIEWPORT_DIMENSIONS.SCALE;
    const arrowAngle = Math.PI / 6;

    const arrowTipX = end.x - padding * Math.cos(angle);
    const arrowTipY = end.y - padding * Math.sin(angle);

    const leftX = arrowTipX - arrowLength * Math.cos(angle - arrowAngle);
    const leftY = arrowTipY - arrowLength * Math.sin(angle - arrowAngle);
    const rightX = arrowTipX - arrowLength * Math.cos(angle + arrowAngle);
    const rightY = arrowTipY - arrowLength * Math.sin(angle + arrowAngle);

    g.moveTo(arrowTipX, arrowTipY);
    g.lineTo(leftX, leftY);
    g.moveTo(arrowTipX, arrowTipY);
    g.lineTo(rightX, rightY);
  };

  const renderStraightSolidLine = (g: PixiGraphics, passerPos: ScreenCoordinate, passPos: ScreenCoordinate, isSelected: boolean) => {
    g.clear();
    applyLineStyle(g, 4, isSelected);

    const dx = passPos.x - passerPos.x;
    const dy = passPos.y - passerPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (!isRenderableDistance(distance)) {
      return;
    }

    const padding = 10 * VIEWPORT_DIMENSIONS.SCALE;
    const paddedDistance = distance - padding;

    const endX = passerPos.x + (dx * paddedDistance) / distance;
    const endY = passerPos.y + (dy * paddedDistance) / distance;

    g.moveTo(passerPos.x, passerPos.y);
    g.lineTo(endX, endY);

    const angle = Math.atan2(dy, dx);
    drawArrowhead(g, passPos, angle, padding);
  };

  const renderCurvedSolidLine = (g: PixiGraphics, passerPos: ScreenCoordinate, passPos: ScreenCoordinate, isSelected: boolean) => {
    g.clear();
    applyLineStyle(g, 4, isSelected);

    const dx = passPos.x - passerPos.x;
    const dy = passPos.y - passerPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (!isRenderableDistance(distance)) {
      return;
    }

    const padding = 10 * VIEWPORT_DIMENSIONS.SCALE;

    const midX = (passerPos.x + passPos.x) / 2;
    const midY = (passerPos.y + passPos.y) / 2;

    const perpX = -dy / distance;
    const perpY = dx / distance;

    const curveIntensity = 75 * VIEWPORT_DIMENSIONS.SCALE;
    const controlX = midX + perpX * curveIntensity;
    const controlY = midY + perpY * curveIntensity;

    g.moveTo(passerPos.x, passerPos.y);
    g.quadraticCurveTo(controlX, controlY, passPos.x, passPos.y);

    const tangentX = passPos.x - controlX;
    const tangentY = passPos.y - controlY;
    const angle = Math.atan2(tangentY, tangentX);

    drawArrowhead(g, passPos, angle, padding);
  };

  const renderStraightDottedLine = (g: PixiGraphics, start: ScreenCoordinate, end: ScreenCoordinate, isSelected: boolean) => {
    g.clear();
    applyLineStyle(g, 2, isSelected);

    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (!isRenderableDistance(distance)) {
      return;
    }

    const padding = 8 * VIEWPORT_DIMENSIONS.SCALE;
    const paddedDistance = distance - padding;
    const paddedDx = dx * (paddedDistance / distance);
    const paddedDy = dy * (paddedDistance / distance);

    const dashLength = 16 * VIEWPORT_DIMENSIONS.SCALE;
    const gapLength = 16 * VIEWPORT_DIMENSIONS.SCALE;
    const cycleLength = dashLength + gapLength;
    const minSegments = 12;
    const maxSegments = 600;
    const stepLengthPx = Math.max(1, Math.min(dashLength, gapLength) / 2);
    const segments = Math.min(maxSegments, Math.max(minSegments, Math.ceil(paddedDistance / stepLengthPx)));

    let prevX = start.x;
    let prevY = start.y;

    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      const x = start.x + paddedDx * t;
      const y = start.y + paddedDy * t;

      const segStartDistance = t * paddedDistance;
      const cyclePos = segStartDistance % cycleLength;
      const isDash = cyclePos < dashLength;

      if (isDash) {
        g.moveTo(prevX, prevY);
        g.lineTo(x, y);
      }

      prevX = x;
      prevY = y;
    }
  };

  return (
    <Container>
      {options.map((option, index) => {
        if (!option || option.passType === ReplayPassType.TURNOVER) return null;

        const previewPayload = buildReplayPreviewPayload(option, replayData, currentDecision.startTime);

        const isSelected = index === selectedCard;
        const { startPosition, passPosition, receiverPosition, passType } = previewPayload.option;
        const isShot = passType === ReplayPassType.SHOT;

        const passerPos = gameToScreenCoords(startPosition.z, startPosition.x);
        const passPos = gameToScreenCoords(passPosition.z, passPosition.x);
        const receiverPos = gameToScreenCoords(receiverPosition.z, receiverPosition.x);
        const adjustedPassPos = isShot
          ? {
              ...passPos,
              x: passPos.x < PITCH_DIMENSIONS.WIDTH / 2 ? 0 : PITCH_DIMENSIONS.WIDTH,
            }
          : passPos;

        const centerX = VIEWPORT_DIMENSIONS.WIDTH / 2 - PITCH_DIMENSIONS.WIDTH / 2;
        const centerY = VIEWPORT_DIMENSIONS.HEIGHT / 2 - PITCH_DIMENSIONS.HEIGHT / 2;

        return (
          <Container key={index} x={centerX} y={centerY}>
            {/* PassPosition */}
            <Graphics x={adjustedPassPos.x} y={adjustedPassPos.y} />
            {/* Player movement dotted line from receiver to pass position */}
            {passType !== ReplayPassType.GROUND && !!passType && !isShot && (
              <Graphics draw={(g: PixiGraphics) => renderStraightDottedLine(g, receiverPos, adjustedPassPos, isSelected)} />
            )}
            {/* Pass line: curved for loft passes and crosses, straight for others */}
            <Graphics
              draw={(g: PixiGraphics) =>
                passType === ReplayPassType.LOFT || passType === ReplayPassType.CROSS
                  ? renderCurvedSolidLine(g, passerPos, adjustedPassPos, isSelected)
                  : renderStraightSolidLine(g, passerPos, adjustedPassPos, isSelected)
              }
            />
          </Container>
        );
      })}
    </Container>
  );
};

export default PassVisualization;
